/**
 * Request/response schemas. These are the contract: the server validates against them
 * and the web client infers its types from them, so a field can never drift between
 * the two tiers.
 */

import { z } from 'zod';
import {
  ACTIVITY_DIRECTIONS,
  ACTIVITY_TYPES,
  CLAIM_STATUSES,
  CLAIM_TIMELINE_ACTIONS,
  CLIENT_DOCUMENT_TYPES,
  CLIENT_TYPES,
  COMMISSION_STATUSES,
  ENDORSEMENT_TYPES,
  INSTALLMENT_STATUSES,
  INSURANCE_TYPES,
  INSURED_ITEM_TYPES,
  LINKABLE_ENTITIES,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  POLICY_DOCUMENT_TYPES,
  POLICY_STATUSES,
  REMINDER_PRIORITIES,
  REMINDER_STATUSES,
  RENEWAL_FREQUENCIES,
  RENEWAL_NOTICE_STATUSES,
  SETTLEMENT_STATUSES,
  USER_ROLES,
  VEHICLE_CATEGORIES,
} from './enums.js';
import { isValidAFM, isValidEmail, isValidGreekPhone, isValidIBAN, isValidPlate } from './validators.js';
import type { RenewalFrequency } from './enums.js';
import type { ProRataCancellation } from './money.js';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from './api.js';

/* ── primitives ─────────────────────────────────────────────────────────────── */

export const uuidSchema = z.string().uuid();

/**
 * Boolean for query strings. Axios serialises `false` as the string "false", and
 * `z.coerce.boolean()` turns any non-empty string — including "false" — back into
 * `true`. This variant reads the string the way every HTTP client writes it.
 */
export const booleanParam = z.preprocess(
  (value) =>
    value === undefined
      ? undefined
      : typeof value === 'string'
        ? !['false', '0', ''].includes(value.toLowerCase())
        : Boolean(value),
  z.boolean().default(false),
);
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date');

/** NUMERIC(12,2) — accepted as a string so precision survives the wire. */
/**
 * A monetary amount, sign allowed.
 *
 * Signed on purpose: an endorsement's premium delta, a cancellation refund and a
 * settlement variance are all legitimately negative. Anything the user *pays* or is
 * *charged* is not — use `positiveMoneySchema` there.
 */
export const moneySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()))
  .refine((v) => /^-?\d{1,10}(\.\d{1,2})?$/.test(v), 'Expected a monetary amount with up to 2 decimals');

/**
 * A monetary amount that cannot be negative.
 *
 * Found in testing: the instalment payment form accepted −500,00 €, stored it, and moved
 * the agency's receivables *up* by €500. A premium, a payment, a claim or a payout is a
 * quantity, never a direction — the sign belongs to the transaction type, not the number.
 */
export const positiveMoneySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()))
  .refine(
    (v) => /^\d{1,10}(\.\d{1,2})?$/.test(v),
    'Το ποσό δεν μπορεί να είναι αρνητικό / Amount cannot be negative',
  );

/** NUMERIC(5,2) — 0.00 … 100.00 */
export const percentageSchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()))
  .refine((v) => /^\d{1,3}(\.\d{1,2})?$/.test(v) && Number(v) <= 100, 'Expected 0–100 with up to 2 decimals');

const optionalText = z.string().trim().max(10_000).optional().nullable();
const shortText = (max: number) => z.string().trim().max(max);

export const emailSchema = z
  .string()
  .trim()
  .max(255)
  .refine(isValidEmail, 'Μη έγκυρη διεύθυνση email / Invalid email address');

export const afmSchema = z
  .string()
  .trim()
  .refine(isValidAFM, 'Μη έγκυρο ΑΦΜ (αποτυγχάνει ο έλεγχος checksum) / Invalid Greek TIN');

export const plateSchema = z
  .string()
  .trim()
  .refine(isValidPlate, 'Μη έγκυρος αριθμός κυκλοφορίας / Invalid registration plate');

export const ibanSchema = z.string().trim().refine(isValidIBAN, 'Μη έγκυρο IBAN / Invalid IBAN');

/**
 * A phone number that is actually a phone number.
 *
 * `isValidGreekPhone` existed in `validators.ts` from the start but was never wired to a
 * schema, so the field was a free-text box: "abc" saved cleanly and then rendered as a
 * `tel:` link. Deliberately permissive beyond Greece — a foreign client's number must
 * not be refused — so anything that reads as an international number passes too. The job
 * here is to reject text, not to police dialling plans.
 */
export const phoneSchema = z
  .string()
  .trim()
  .refine(
    (value) => isValidGreekPhone(value) || /^\+?\d[\d\s()-]{6,18}\d$/.test(value),
    'Μη έγκυρος αριθμός τηλεφώνου / Invalid phone number',
  );

/** Optional phone: absent, empty, or valid — an empty box must not be an error. */
const optionalPhone = z.union([phoneSchema, z.literal('')]).optional().nullable();

/**
 * Rejects a reversed date range.
 *
 * A filter with `from` after `to` matched nothing, so every KPI on the page silently
 * read zero and looked like a catastrophic month rather than a typo.
 */
export function withOrderedDates<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (value: unknown) => {
      const v = value as { dateFrom?: string; dateTo?: string };
      return !v.dateFrom || !v.dateTo || v.dateFrom <= v.dateTo;
    },
    {
      message: 'Η ημερομηνία «από» πρέπει να προηγείται της «έως» / Start date must precede end date',
      path: ['dateFrom'],
    },
  );
}

/**
 * Client-generated idempotency key, present only on writes that were queued offline.
 * The server dedupes on it so a replayed delivery returns the original row instead of
 * creating a second one — see `offline.ts`.
 */
const clientRefField = uuidSchema.optional();

/* ── list query ─────────────────────────────────────────────────────────────── */

export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  sortBy: z.string().max(64).optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
  search: z.string().trim().max(200).optional(),
});
export type ListQueryInput = z.input<typeof listQuerySchema>;

/** Comma-separated repeatable filter, e.g. `?status=active,renewal_due`. */
const csvEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : undefined))
    .pipe(z.array(z.enum(values)).optional());

const csvString = z
  .string()
  .optional()
  .transform((v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : undefined));

/* ── auth ───────────────────────────────────────────────────────────────────── */

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required').max(200),
  rememberMe: z.boolean().optional().default(false),
});
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * The one password rule, mirrored by InputValidator.Password on the server. Shared by the
 * change-password form, the admin reset dialog and the emailed-link reset, so the three
 * screens cannot drift into accepting different passwords.
 */
export const strongPasswordSchema = z
  .string()
  .min(10, 'Τουλάχιστον 10 χαρακτήρες / At least 10 characters')
  .max(200)
  .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v),
    'Χρειάζεται πεζά, κεφαλαία και αριθμό / Needs lower, upper and a digit');

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: strongPasswordSchema,
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** «Ξεχάσατε τον κωδικό σας;» — just the address; the answer is the same either way. */
export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/** The emailed link's token plus the new password. */
export const resetPasswordSchema = z.object({
  token: z.string().min(1).max(500),
  password: strongPasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/* ── users ──────────────────────────────────────────────────────────────────── */

export const createUserSchema = z.object({
  email: emailSchema,
  fullName: shortText(255).min(2),
  role: z.enum(USER_ROLES),
  password: z.string().min(10).max(200),
  phone: optionalPhone,
  /** Producer commission share (%) for the sub-agent/partner split — benchmark gap #7. */
  commissionSharePercentage: percentageSchema.optional().nullable(),
  isActive: z.boolean().default(true),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = createUserSchema.partial().omit({ password: true });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/*
 * `UserDto` and `AuthSessionDto` used to be declared here, by hand, next to the input schemas.
 *
 * They now come from `./api`, generated from the C# contracts — because auth is served by the .NET
 * API, and a hand-written copy of someone else's response shape is a claim that goes stale in
 * silence. The input schemas above stay: those describe what this application sends, and zod earns
 * its place by validating before the request leaves.
 */

/* ── insurance companies ────────────────────────────────────────────────────── */

export const insuranceCompanySchema = z.object({
  name: shortText(255).min(2),
  code: shortText(50).optional().nullable(),
  contactPhone: optionalPhone,
  contactEmail: z.union([emailSchema, z.literal('')]).optional().nullable(),
  /**
   * Numbers the agent gives to a *client* — the carrier's accident line, roadside assistance
   * and out-of-hours desk. Separate from `contactPhone`, which reaches the agency's account
   * manager and is the wrong number to hand someone standing next to a damaged car.
   */
  claimsPhone: optionalPhone,
  roadsidePhone: optionalPhone,
  emergencyPhone: optionalPhone,
  /** The carrier's portal — where its renewal notices arrive. */
  portalUrl: z.union([z.string().trim().url().max(500), z.literal('')]).optional().nullable(),
  /** Default commission rate applied when creating a policy for this insurer. */
  defaultCommissionPercentage: percentageSchema.optional().nullable(),
  /** Days after period end within which the insurer settles commission. */
  settlementDays: z.coerce.number().int().min(0).max(365).optional().nullable(),
  notes: optionalText,
  isActive: z.boolean().default(true),
});
export type InsuranceCompanyInput = z.infer<typeof insuranceCompanySchema>;

/**
 * The carrier's commission grid: one rate per vehicle category.
 *
 * Sent as a whole set rather than row by row, because that is how it is decided and how it is
 * read — «η Υδρόγειος πληρώνει 18 στα ΙΧ, 22 στα ΜΟΤΟ, 12 στα ενοικιαζόμενα» is one negotiation,
 * not three. The server replaces the carrier's rates with exactly what arrives, so removing a
 * category means omitting its row.
 */
export const insurerCommissionRateSchema = z.object({
  vehicleCategory: z.enum(VEHICLE_CATEGORIES),
  commissionPercentage: percentageSchema,
  notes: shortText(500).optional().nullable(),
});
export type InsurerCommissionRateInput = z.infer<typeof insurerCommissionRateSchema>;

export const insurerCommissionRatesSchema = z
  .object({ rates: z.array(insurerCommissionRateSchema).max(VEHICLE_CATEGORIES.length) })
  .refine(
    (v) => new Set(v.rates.map((r) => r.vehicleCategory)).size === v.rates.length,
    { message: 'Κάθε κατηγορία μία φορά / One rate per category', path: ['rates'] },
  );
export type InsurerCommissionRatesInput = z.infer<typeof insurerCommissionRatesSchema>;

export interface InsurerCommissionRateDto {
  id: string;
  insuranceCompanyId: string;
  vehicleCategory: (typeof VEHICLE_CATEGORIES)[number];
  commissionPercentage: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Picks the rate a new policy should start from: the carrier's rate for that category, falling
 * back to its single default.
 *
 * Lives here rather than in the form because the insurer screen needs the same answer — it shows
 * which categories actually have a rate and which fall through to the default — and two copies of
 * the fallback rule would drift. Returns null when neither is known: the form then leaves the
 * field blank, which is an obvious omission, rather than filling a confident 0% that produces
 * commissions of nothing.
 */
export function resolveCommissionRate(
  company: Pick<InsuranceCompanyDto, 'defaultCommissionPercentage' | 'commissionRates'> | undefined,
  vehicleCategory: (typeof VEHICLE_CATEGORIES)[number] | null | undefined,
): string | null {
  if (!company) return null;
  const match = vehicleCategory
    ? company.commissionRates?.find((rate) => rate.vehicleCategory === vehicleCategory)
    : undefined;
  return match?.commissionPercentage ?? company.defaultCommissionPercentage ?? null;
}

export interface InsuranceCompanyDto {
  id: string;
  name: string;
  code: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  /** Client-facing carrier lines — see `insuranceCompanySchema`. */
  claimsPhone: string | null;
  roadsidePhone: string | null;
  emergencyPhone: string | null;
  portalUrl: string | null;
  /** The fallback rate: what a policy with no vehicle uses, and what a vehicle policy (motor or
   *  roadside assistance) in a category with no row in `commissionRates` uses. */
  defaultCommissionPercentage: string | null;
  settlementDays: number | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  policyCount?: number;
  /** Per-category rates, carried on the carrier so the policy form can resolve a rate without
   *  a request per keystroke. Absent on responses that do not join them. */
  commissionRates?: InsurerCommissionRateDto[];
}

/* ── clients ────────────────────────────────────────────────────────────────── */

export const createClientSchema = z
  .object({
    clientType: z.enum(CLIENT_TYPES),
    fullName: shortText(255).min(2, 'Απαιτείται ονοματεπώνυμο / Name is required'),
    taxNumber: z.union([afmSchema, z.literal('')]).optional().nullable(),
    idNumber: shortText(50).optional().nullable(),
    drivingLicenseNumber: shortText(50).optional().nullable(),
    dateOfBirth: isoDateSchema.optional().nullable(),
    contactPerson: shortText(255).optional().nullable(),
    phone: optionalPhone,
    email: z.union([emailSchema, z.literal('')]).optional().nullable(),
    address: optionalText,
    city: shortText(100).optional().nullable(),
    postalCode: shortText(20).optional().nullable(),
    iban: z.union([ibanSchema, z.literal('')]).optional().nullable(),
    notes: optionalText,
    tags: z.array(shortText(255)).max(20).default([]),
    assignedAgentId: uuidSchema.optional().nullable(),
    /** GDPR: explicit consent for marketing contact, with the date it was given. */
    marketingConsent: z.boolean().default(false),
  })
  .refine((v) => v.clientType !== 'individual' || !v.contactPerson, {
    message: 'Το πεδίο υπεύθυνου επικοινωνίας αφορά μόνο εταιρείες / Contact person applies to companies only',
    path: ['contactPerson'],
  });
export type CreateClientInput = z.infer<typeof createClientSchema>;

export const updateClientSchema = createClientSchema.innerType().partial();
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

export const clientListQuerySchema = listQuerySchema.extend({
  clientType: z.enum(CLIENT_TYPES).optional(),
  city: z.string().trim().max(100).optional(),
  tags: csvString,
  assignedAgentId: uuidSchema.optional(),
  hasActivePolicies: booleanParam,
});

export interface ClientDto {
  id: string;
  clientType: (typeof CLIENT_TYPES)[number];
  fullName: string;
  taxNumber: string | null;
  idNumber: string | null;
  drivingLicenseNumber: string | null;
  dateOfBirth: string | null;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  iban: string | null;
  notes: string | null;
  tags: string[];
  assignedAgentId: string | null;
  assignedAgentName?: string | null;
  marketingConsent: boolean;
  marketingConsentAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Aggregates supplied by the list/detail endpoints. */
  activePolicyCount?: number;
  totalPremium?: string;
  openClaimCount?: number;
  /** Distinct plates across the whole archive — one per car, not per annual policy. */
  vehicleCount?: number;
  /** How many of those are on a live policy right now. */
  activeVehicleCount?: number;
}

/* ── documents ──────────────────────────────────────────────────────────────── */

export const clientDocumentUploadSchema = z.object({
  documentType: z.enum(CLIENT_DOCUMENT_TYPES),
  notes: optionalText,
  /** Expiry for ID cards, licences and green cards — drives a renewal-style alert. */
  expiresAt: isoDateSchema.optional().nullable(),
});

export const policyDocumentUploadSchema = z.object({
  documentType: z.enum(POLICY_DOCUMENT_TYPES),
  notes: optionalText,
});

export interface DocumentDto {
  id: string;
  fileName: string;
  filePath: string;
  fileSize: number | null;
  mimeType: string | null;
  documentType: string;
  notes: string | null;
  expiresAt?: string | null;
  uploadedBy: string | null;
  uploadedByName?: string | null;
  uploadedAt: string;
}

/* ── insured items (benchmark gap #3) ───────────────────────────────────────── */

export const insuredItemSchema = z
  .object({
    itemType: z.enum(INSURED_ITEM_TYPES),
    description: shortText(255).min(1),
    /** Vehicles */
    plateNumber: z.union([plateSchema, z.literal('')]).optional().nullable(),
    make: shortText(100).optional().nullable(),
    model: shortText(100).optional().nullable(),
    year: z.coerce.number().int().min(1900).max(2100).optional().nullable(),
    vin: shortText(50).optional().nullable(),
    engineCc: z.coerce.number().int().min(0).max(100_000).optional().nullable(),
    /** Properties */
    addressLine: shortText(255).optional().nullable(),
    squareMeters: z.coerce.number().int().min(0).max(1_000_000).optional().nullable(),
    /** Common */
    insuredValue: positiveMoneySchema.optional().nullable(),
    /** Bonus-Malus class (vehicles) — asked on every Greek auto renewal. */
    bmClass: z.coerce.number().int().min(0).max(30).optional().nullable(),
    metadata: z.record(z.unknown()).optional().nullable(),
  })
  .refine((v) => v.itemType !== 'vehicle' || !!v.plateNumber, {
    message: 'Ο αριθμός κυκλοφορίας είναι υποχρεωτικός για οχήματα / Plate is required for vehicles',
    path: ['plateNumber'],
  });
export type InsuredItemInput = z.infer<typeof insuredItemSchema>;

export interface InsuredItemDto {
  id: string;
  policyId: string;
  itemType: (typeof INSURED_ITEM_TYPES)[number];
  description: string;
  plateNumber: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  engineCc: number | null;
  addressLine: string | null;
  squareMeters: number | null;
  insuredValue: string | null;
  bmClass: number | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/* ── policies ───────────────────────────────────────────────────────────────── */

/*
 * The policy's fields, before the cross-field rules.
 *
 * Named separately because `updatePolicySchema` needs the object and `createPolicySchema` needs
 * the object plus three refinements — and `.refine()` returns a wrapper whose `.partial()` does
 * not exist. The alternative, peeling the wrappers off with `.innerType()`, needed one more call
 * every time a rule was added and silently pointed at the wrong layer if you miscounted.
 */
const policyFields = z
  .object({
    clientId: uuidSchema,
    insuranceCompanyId: uuidSchema,
    insuranceType: z.enum(INSURANCE_TYPES),
    policyNumber: shortText(100).min(1, 'Απαιτείται αριθμός συμβολαίου / Policy number is required'),
    /**
     * Αριθμός ανανεωτηρίου — optional, because insurers that reuse the central number issue
     * none. Not validated against a format: the shape varies per carrier, and a regex here
     * would reject real paperwork.
     */
    renewalNumber: shortText(100).optional().nullable(),
    startDate: isoDateSchema,
    endDate: isoDateSchema.optional(),
    renewalFrequency: z.enum(RENEWAL_FREQUENCIES),
    /**
     * ΟΛΙΚΑ (μεικτά) — what the client pays. Premium plus φόρος ασφαλίστρων, ΤΕΟ/Επικουρικό,
     * δικαίωμα συμβολαίου and χαρτόσημο. This is the figure the instalments split.
     */
    premiumAmount: positiveMoneySchema,
    /**
     * ΚΑΘΑΡΑ — the premium alone, and the only base the commission is calculated on.
     *
     * Optional at the API boundary although the office form asks for it, and the two reasons are
     * different: the archive importer writes policies from documents that print the ολικά and not
     * the καθαρά, and half the existing book has none. Sending nothing leaves it NULL, which reads
     * as «λείπουν» and shows a commission of 0 — sending a guess would not.
     */
    netPremiumAmount: positiveMoneySchema.optional().nullable(),
    commissionPercentage: percentageSchema,
    /**
     * The vehicle's tariff category — the key that picks the carrier's commission rate.
     *
     * Only meaningful on auto policies, and left optional rather than conditionally required:
     * the whole existing book has none yet, and a required field would block every edit of it.
     */
    vehicleCategory: z.enum(VEHICLE_CATEGORIES).optional().nullable(),
    coverageDescription: optionalText,
    status: z.enum(POLICY_STATUSES).default('active'),
    paymentStatus: z.enum(PAYMENT_STATUSES).default('unpaid'),
    notes: optionalText,
    assignedAgentId: uuidSchema.optional().nullable(),
    /** Number of premium instalments for the period — benchmark gap #4. */
    installmentCount: z.coerce.number().int().min(1).max(12).default(1),
    renewalLeadDays: z.coerce.number().int().min(0).max(180).default(30),
    insuredItems: z.array(insuredItemSchema).max(50).default([]),
  });

export const createPolicySchema = policyFields
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'Η λήξη πρέπει να έπεται της έναρξης / End date must not precede start date',
    path: ['endDate'],
  })
  .refine((v) => v.renewalFrequency !== 'one_off' || !!v.endDate, {
    message: 'Τα εφάπαξ συμβόλαια απαιτούν ημερομηνία λήξης / One-off policies need an explicit end date',
    path: ['endDate'],
  })
  /*
   * Καθαρά can never exceed ολικά — the difference is tax and levies the carrier collects on top.
   * Caught here as well as by `ck_policies_net_premium` because the common way to trip it is
   * mundane: filling the καθαρά in on one of the 341 imported policies whose ολικά came through
   * as 0. That deserves a field error, not a constraint violation.
   */
  .refine((v) => !v.netPremiumAmount || Number(v.netPremiumAmount) <= Number(v.premiumAmount), {
    message: 'Τα καθαρά δεν μπορούν να ξεπερνούν τα ολικά / Net premium cannot exceed the gross',
    path: ['netPremiumAmount'],
  });
export type CreatePolicyInput = z.infer<typeof createPolicySchema>;

export const updatePolicySchema = policyFields
  .partial()
  .omit({ insuredItems: true })
  /*
   * The same καθαρά ≤ ολικά rule as on create, but guarded on BOTH being present.
   *
   * A partial patch may legitimately carry one without the other — the API accepts
   * `{ netPremiumAmount }` alone — and `Number(undefined)` is NaN, which compares false and would
   * reject a perfectly valid request. The edit form always holds both, so this is what gives it
   * the error on the field instead of a round trip to find out.
   */
  .refine(
    (v) =>
      v.netPremiumAmount == null ||
      v.premiumAmount == null ||
      Number(v.netPremiumAmount) <= Number(v.premiumAmount),
    {
      message: 'Τα καθαρά δεν μπορούν να ξεπερνούν τα ολικά / Net premium cannot exceed the gross',
      path: ['netPremiumAmount'],
    },
  );
export type UpdatePolicyInput = z.infer<typeof updatePolicySchema>;

/**
 * Recording the ειδοποιητήριο call.
 *
 * `notifiedAt` is not accepted from the client: the whole value of this record is that it is
 * evidence, and a caller-supplied timestamp is a claim rather than evidence. The server
 * stamps it, and the acting user is taken from the session for the same reason.
 */
export const updateRenewalNoticeSchema = z.object({
  noticeStatus: z.enum(RENEWAL_NOTICE_STATUSES),
  /** When the carrier's notice arrived — genuinely known only to the operator. */
  noticeReceivedAt: isoDateSchema.optional().nullable(),
  noticeNote: optionalText,
});
export type UpdateRenewalNoticeInput = z.infer<typeof updateRenewalNoticeSchema>;

export const policyListQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  /** Drives the ειδοποιητήρια worklist — «what still needs a call». */
  noticeStatus: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : undefined))
    .pipe(z.array(z.enum(RENEWAL_NOTICE_STATUSES)).optional()),
  insuranceCompanyId: uuidSchema.optional(),
  insuranceType: csvEnum(INSURANCE_TYPES),
  status: csvEnum(POLICY_STATUSES),
  paymentStatus: csvEnum(PAYMENT_STATUSES),
  assignedAgentId: uuidSchema.optional(),
  /** `YYYY-MM` — policies expiring within that month. */
  expirationMonth: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  endDateFrom: isoDateSchema.optional(),
  endDateTo: isoDateSchema.optional(),
  /** Free-text plate lookup across insured items. */
  plateNumber: z.string().trim().max(20).optional(),
});

export const updatePaymentStatusSchema = z.object({
  paymentStatus: z.enum(PAYMENT_STATUSES),
  paymentDate: isoDateSchema.optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  notes: optionalText,
  /** Premium for the *next* period, when the insurer has repriced it. */
  nextPremiumAmount: moneySchema.optional().nullable(),
  /** And its καθαρά — carried forward unchanged when omitted, like the gross. A repriced
   *  renewal moves both, and sending only the gross would leave the commission on last
   *  year's base. */
  nextNetPremiumAmount: moneySchema.optional().nullable(),
  nextCommissionPercentage: percentageSchema.optional().nullable(),
  /**
   * The ανανεωτήριο number for the *next* period — the number on the certificate the agent
   * has just been sent. Named `next…` like the two above because it belongs to the period
   * being opened, not the one being archived: the closing period keeps whatever number it
   * already carried, snapshotted into its archive row.
   */
  nextRenewalNumber: shortText(100).optional().nullable(),
  /**
   * Idempotency key, minted by the client once per intent to collect.
   *
   * Renewal is the one transition that cannot safely be repeated: it archives a period
   * and advances the policy a year. A double-click, or a retry after the response was
   * lost, would otherwise do it twice and book the commission twice. Optional so that
   * scripted and bulk callers still work, but the UI always sends one.
   */
  clientRef: uuidSchema.optional(),
});
export type UpdatePaymentStatusInput = z.infer<typeof updatePaymentStatusSchema>;

export const cancelPolicySchema = z.object({
  reason: shortText(500).min(3, 'Απαιτείται αιτιολογία / A reason is required'),
  effectiveDate: isoDateSchema.optional(),
});

export const bulkPolicyActionSchema = z.object({
  policyIds: z.array(uuidSchema).min(1).max(500),
  action: z.enum(['mark_paid', 'mark_unpaid', 'cancel', 'assign_agent']),
  assignedAgentId: uuidSchema.optional().nullable(),
  reason: shortText(500).optional(),
  /**
   * Idempotency key for the whole batch.
   *
   * `mark_paid` runs the renewal transition once per policy, so a repeated delivery of a
   * 25-row batch renews twenty-five policies twice and books the commission twice on each
   * — the single-policy dialog was protected against exactly this and the bulk bar was
   * not. The server derives a per-policy key from this one so each policy dedupes on its
   * own while the batch stays a single user intent.
   */
  clientRef: uuidSchema.optional(),
});
export type BulkPolicyActionInput = z.infer<typeof bulkPolicyActionSchema>;

/* ── ειδοποιητήριο by email ──────────────────────────────────────────────────── */

/**
 * The agency's renewal-email settings.
 *
 * One decision expressed in four fields: whether notices go out at all, whether the system sends
 * them by itself or waits for someone to press send, how far ahead, and in what words.
 */
export const RENEWAL_EMAIL_MODES = ['manual', 'auto'] as const;
export type RenewalEmailMode = (typeof RENEWAL_EMAIL_MODES)[number];

export const renewalEmailSettingsSchema = z.object({
  enabled: z.boolean(),
  mode: z.enum(RENEWAL_EMAIL_MODES),
  /**
   * Days before expiry the automatic pass sends. The upper bound is the same 120 the server
   * enforces; the lower is 1 rather than 0, because a notice that arrives on the expiry date is
   * not a notice.
   */
  leadDays: z.coerce.number().int().min(1).max(120),
  subject: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(10_000),
  senderName: z.string().trim().max(255).nullish(),
  replyTo: z.string().trim().max(255).nullish(),
});
export type UpdateRenewalEmailSettingsInput = z.infer<typeof renewalEmailSettingsSchema>;

export interface RenewalEmailSettingsDto extends UpdateRenewalEmailSettingsInput {
  updatedAt: string;
  updatedBy: string | null;
  updatedByName?: string | null;
  /** False means the server has no SMTP host and writes mail to its log instead of sending it. */
  mailConfigured: boolean;
  /** The placeholder names the server will substitute — the editor lists these rather than
      hard-coding a copy that can drift from what actually renders. */
  placeholders: string[];
}

/** One notice, rendered exactly as it would be sent. */
export interface RenewalEmailPreviewDto {
  policyId: string;
  policyNumber: string;
  clientName: string;
  recipient: string | null;
  subject: string;
  body: string;
  /** Why this policy cannot be emailed, already translated — or null when it can. */
  blocker: string | null;
  alreadySentAt: string | null;
}

export interface SendRenewalEmailResultDto {
  policyId: string;
  sent: boolean;
  recipient: string | null;
  reason: string | null;
  policy?: PolicyDto | null;
}

export interface RenewalEmailBatchDto {
  sent: number;
  skipped: number;
  results: SendRenewalEmailResultDto[];
}

export interface PolicyDto {
  id: string;
  clientId: string;
  clientName?: string;
  /** Joined for the ειδοποιητήρια call sheet — that screen exists to place a phone call. */
  clientPhone?: string | null;
  /** Joined for the same sheet's other channel. Null is why a row's send button is disabled. */
  clientEmail?: string | null;
  insuranceCompanyId: string;
  insuranceCompanyName?: string;
  insuranceType: (typeof INSURANCE_TYPES)[number];
  policyNumber: string;
  /** Αριθμός ανανεωτηρίου for the current period; null when the insurer issues none. */
  renewalNumber: string | null;
  /** Ειδοποιητήριο state for the period in flight. */
  noticeStatus: (typeof RENEWAL_NOTICE_STATUSES)[number];
  noticeReceivedAt: string | null;
  notifiedAt: string | null;
  notifiedBy: string | null;
  notifiedByName?: string | null;
  noticeNote: string | null;
  /** When the ειδοποιητήριο was emailed for the period in flight; null means it has not been. */
  noticeEmailSentAt?: string | null;
  noticeEmailSentTo?: string | null;
  startDate: string;
  endDate: string;
  renewalFrequency: (typeof RENEWAL_FREQUENCIES)[number];
  /** ΟΛΙΚΑ — what the client pays. */
  premiumAmount: string;
  /** ΚΑΘΑΡΑ — the commission base. Null means it has not been recorded yet, which is
   *  why `commissionAmount` reads 0.00 on so much of the imported book. */
  netPremiumAmount: string | null;
  commissionPercentage: string;
  commissionAmount: string;
  commissionStatus: (typeof COMMISSION_STATUSES)[number];
  /** Tariff category of the insured vehicle; null on non-auto policies and on anything
   *  written before the categories existed. */
  vehicleCategory: (typeof VEHICLE_CATEGORIES)[number] | null;
  coverageDescription: string | null;
  status: (typeof POLICY_STATUSES)[number];
  paymentStatus: (typeof PAYMENT_STATUSES)[number];
  nextRenewalDate: string | null;
  renewalLeadDays: number;
  installmentCount: number;
  notes: string | null;
  assignedAgentId: string | null;
  assignedAgentName?: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  cancellationRefund: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Derived server-side so sorting and colouring agree with the list query. */
  daysUntilExpiry?: number;
  currentPeriodNumber?: number;
  insuredItems?: InsuredItemDto[];
  openClaimCount?: number;
  /** Normalised, de-duplicated plates of this policy's vehicles; empty for a health or
   *  property policy. Present on list rows, so a row can name the car it insures. */
  plates?: string[];
  /** The later policy that already covers the next period; set means «already renewed». */
  successorPolicyNumber?: string | null;
}

export interface PolicyRenewalDto {
  id: string;
  policyId: string;
  periodNumber: number;
  /** The certificate number that covered this archived period. */
  renewalNumber: string | null;
  /** How this period's ειδοποιητήριο ended — the after-the-fact record of the call. */
  noticeStatus: (typeof RENEWAL_NOTICE_STATUSES)[number] | null;
  notifiedAt: string | null;
  notifiedBy: string | null;
  notifiedByName?: string | null;
  startDate: string;
  endDate: string;
  premiumAmount: string;
  /** The καθαρά this period's commission was booked on; null for periods archived before
   *  the split existed. */
  netPremiumAmount: string | null;
  commissionPercentage: string;
  commissionAmount: string;
  paymentStatus: (typeof PAYMENT_STATUSES)[number];
  paymentDate: string | null;
  paymentMethod: string | null;
  renewedBy: string | null;
  renewedByName?: string | null;
  notes: string | null;
  createdAt: string;
}

/** Server-computed preview shown in the "Mark as paid" modal before committing. */
export interface RenewalPreviewDto {
  willRenew: boolean;
  reason?: string;
  archivedPeriod: {
    startDate: string;
    endDate: string;
    premiumAmount: string;
    /** ΚΑΘΑΡΑ — the commission base. Null on the 4.894 imported policies that have none yet. */
    netPremiumAmount: string | null;
    commissionAmount: string;
  };
  nextPeriod: {
    startDate: string;
    endDate: string;
    nextRenewalDate: string;
    premiumAmount: string;
    netPremiumAmount: string | null;
    commissionPercentage: string;
    commissionAmount: string;
    /** The frequency the period is computed on — the current one unless changed at renewal. */
    renewalFrequency: RenewalFrequency;
  } | null;
}

/* ── endorsements (benchmark gap #6) ────────────────────────────────────────── */

export const createEndorsementSchema = z.object({
  endorsementType: z.enum(ENDORSEMENT_TYPES),
  endorsementNumber: shortText(100).optional().nullable(),
  effectiveDate: isoDateSchema,
  description: shortText(1000).min(3),
  /** Signed delta applied to the policy premium — negative for refunds. */
  premiumDelta: moneySchema.default('0.00'),
  newPremiumAmount: moneySchema.optional().nullable(),
  /** The new καθαρά. Without it a πρόσθετη πράξη that raises the premium would raise what
   *  the client pays and leave the commission untouched. */
  newNetPremiumAmount: moneySchema.optional().nullable(),
  newCommissionPercentage: percentageSchema.optional().nullable(),
  newRenewalFrequency: z.enum(RENEWAL_FREQUENCIES).optional().nullable(),
  notes: optionalText,
});
export type CreateEndorsementInput = z.infer<typeof createEndorsementSchema>;

export interface EndorsementDto {
  id: string;
  policyId: string;
  endorsementType: (typeof ENDORSEMENT_TYPES)[number];
  endorsementNumber: string | null;
  effectiveDate: string;
  description: string;
  premiumDelta: string;
  previousPremiumAmount: string;
  newPremiumAmount: string;
  previousCommissionPercentage: string;
  newCommissionPercentage: string;
  notes: string | null;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
}

/* ── instalments (benchmark gap #4) ─────────────────────────────────────────── */

export const recordInstallmentPaymentSchema = z.object({
  paidAmount: positiveMoneySchema,
  paymentDate: isoDateSchema,
  paymentMethod: z.enum(PAYMENT_METHODS),
  reference: shortText(100).optional().nullable(),
  notes: optionalText,
});
export type RecordInstallmentPaymentInput = z.infer<typeof recordInstallmentPaymentSchema>;

export interface InstallmentDto {
  id: string;
  policyId: string;
  policyNumber?: string;
  /** Present only on the cross-policy collections worklist, like the three fields below it. */
  plates?: string[] | null;
  clientId?: string;
  clientName?: string;
  clientPhone?: string | null;
  daysOverdue?: number;
  installmentNumber: number;
  totalInstallments: number;
  dueDate: string;
  amount: string;
  paidAmount: string | null;
  paymentDate: string | null;
  paymentMethod: string | null;
  reference: string | null;
  status: (typeof INSTALLMENT_STATUSES)[number];
  notes: string | null;
  createdAt: string;
}

/* ── commission settlements (benchmark gap #5) ──────────────────────────────── */

export const createSettlementSchema = z.object({
  insuranceCompanyId: uuidSchema,
  statementReference: shortText(100).min(1),
  statementDate: isoDateSchema,
  periodFrom: isoDateSchema,
  periodTo: isoDateSchema,
  declaredAmount: positiveMoneySchema,
  notes: optionalText,
});
export type CreateSettlementInput = z.infer<typeof createSettlementSchema>;

export const reconcileSettlementSchema = z.object({
  /** Policy ids whose expected commission this statement covers. */
  policyIds: z.array(uuidSchema).min(1).max(2000),
});

export interface SettlementDto {
  id: string;
  insuranceCompanyId: string;
  insuranceCompanyName?: string;
  statementReference: string;
  statementDate: string;
  periodFrom: string;
  periodTo: string;
  declaredAmount: string;
  expectedAmount: string;
  /** declaredAmount − expectedAmount; the number the back office actually chases. */
  varianceAmount: string;
  matchedPolicyCount: number;
  status: (typeof SETTLEMENT_STATUSES)[number];
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Server-computed ακυρωτικό, shown in the cancel dialog before the operator commits. */
export interface CancellationPreviewDto extends ProRataCancellation {
  effectiveDate: string;
  /** A refund only arises when the period was actually paid. */
  premiumPaid: boolean;
}

/* ── claims ─────────────────────────────────────────────────────────────────── */

export const createClaimSchema = z
  .object({
    policyId: uuidSchema,
    claimNumber: shortText(100).optional().nullable(),
    incidentDate: isoDateSchema,
    reportDate: isoDateSchema.optional(),
    description: shortText(2000).optional().nullable(),
    claimAmount: positiveMoneySchema.optional().nullable(),
    payoutAmount: positiveMoneySchema.optional().nullable(),
    status: z.enum(CLAIM_STATUSES).default('open'),
    notes: optionalText,
    assignedAgentId: uuidSchema.optional().nullable(),
    insuredItemId: uuidSchema.optional().nullable(),
  })
  .refine((v) => !v.reportDate || v.reportDate >= v.incidentDate, {
    message: 'Η δήλωση δεν μπορεί να προηγείται του συμβάντος / Report date cannot precede the incident',
    path: ['reportDate'],
  });
export type CreateClaimInput = z.infer<typeof createClaimSchema>;

export const updateClaimSchema = createClaimSchema.innerType().partial().omit({ policyId: true });
export type UpdateClaimInput = z.infer<typeof updateClaimSchema>;

export const updateClaimStatusSchema = z.object({
  status: z.enum(CLAIM_STATUSES),
  note: shortText(1000).optional().nullable(),
  payoutAmount: positiveMoneySchema.optional().nullable(),
});

export const claimListQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  policyId: uuidSchema.optional(),
  insuranceCompanyId: uuidSchema.optional(),
  status: csvEnum(CLAIM_STATUSES),
  assignedAgentId: uuidSchema.optional(),
  incidentDateFrom: isoDateSchema.optional(),
  incidentDateTo: isoDateSchema.optional(),
});

export interface ClaimDto {
  id: string;
  policyId: string;
  policyNumber?: string;
  /** Plates of the vehicles on the claimed policy — the claim is labelled by them. */
  plates?: string[] | null;
  clientId: string;
  clientName?: string;
  insuranceCompanyId: string;
  insuranceCompanyName?: string;
  insuranceType?: string;
  claimNumber: string | null;
  incidentDate: string;
  reportDate: string;
  description: string | null;
  claimAmount: string | null;
  payoutAmount: string | null;
  status: (typeof CLAIM_STATUSES)[number];
  notes: string | null;
  assignedAgentId: string | null;
  assignedAgentName?: string | null;
  insuredItemId: string | null;
  closedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Ageing in days — open claims measure to today, closed ones to their close date. */
  daysOpen?: number;
  documentCount?: number;
}

export const createTimelineEntrySchema = z.object({
  clientRef: clientRefField,
  actionType: z.enum(CLAIM_TIMELINE_ACTIONS),
  description: shortText(2000).min(1),
  metadata: z.record(z.unknown()).optional().nullable(),
});

export interface ClaimTimelineDto {
  id: string;
  claimId: string;
  actionType: (typeof CLAIM_TIMELINE_ACTIONS)[number];
  description: string;
  metadata: Record<string, unknown> | null;
  /** Distinguishes system-generated entries from operator notes (spec §8.9). */
  isSystem: boolean;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
}

/* ── reminders ──────────────────────────────────────────────────────────────── */

export const createReminderSchema = z
  .object({
    clientRef: clientRefField,
    title: shortText(255).min(2),
    reminderDate: isoDateSchema,
    notes: optionalText,
    priority: z.enum(REMINDER_PRIORITIES).default('normal'),
    clientId: uuidSchema.optional().nullable(),
    policyId: uuidSchema.optional().nullable(),
    claimId: uuidSchema.optional().nullable(),
    assignedTo: uuidSchema.optional().nullable(),
  })
  .refine((v) => !!(v.clientId || v.policyId || v.claimId) || true, { message: 'ok' });
export type CreateReminderInput = z.infer<typeof createReminderSchema>;

export const updateReminderSchema = createReminderSchema.innerType().partial().extend({
  status: z.enum(REMINDER_STATUSES).optional(),
});

export const reminderListQuerySchema = listQuerySchema.extend({
  status: csvEnum(REMINDER_STATUSES),
  priority: csvEnum(REMINDER_PRIORITIES),
  assignedTo: uuidSchema.optional(),
  entityType: z.enum(LINKABLE_ENTITIES).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  /**
   * Scope to one record. The client, policy and claim detail pages all ask for "the
   * reminders on *this*"; without these the query silently widened to the whole agency
   * and showed every user another client's follow-ups.
   */
  clientId: uuidSchema.optional(),
  policyId: uuidSchema.optional(),
  claimId: uuidSchema.optional(),
});

export interface ReminderDto {
  id: string;
  title: string;
  reminderDate: string;
  status: (typeof REMINDER_STATUSES)[number];
  priority: (typeof REMINDER_PRIORITIES)[number];
  notes: string | null;
  clientId: string | null;
  policyId: string | null;
  claimId: string | null;
  /** Resolved label + route for the "Related Entity" column (spec §8.10). */
  relatedEntityType: (typeof LINKABLE_ENTITIES)[number] | null;
  relatedEntityId: string | null;
  relatedEntityLabel: string | null;
  assignedTo: string | null;
  assignedToName?: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  daysOverdue?: number;
}

/* ── activities (benchmark gap #9) ──────────────────────────────────────────── */

export const createActivitySchema = z.object({
  clientRef: clientRefField,
  clientId: uuidSchema,
  activityType: z.enum(ACTIVITY_TYPES),
  direction: z.enum(ACTIVITY_DIRECTIONS).default('outbound'),
  subject: shortText(255).min(1),
  body: optionalText,
  occurredAt: z.string().datetime().optional(),
  policyId: uuidSchema.optional().nullable(),
  claimId: uuidSchema.optional().nullable(),
});
export type CreateActivityInput = z.infer<typeof createActivitySchema>;

export interface ActivityDto {
  id: string;
  clientId: string;
  activityType: (typeof ACTIVITY_TYPES)[number];
  direction: (typeof ACTIVITY_DIRECTIONS)[number];
  subject: string;
  body: string | null;
  occurredAt: string;
  policyId: string | null;
  claimId: string | null;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
}

/* ── dashboard ──────────────────────────────────────────────────────────────── */

/**
 * Left as a plain object so routers can still `.extend()` it — `.refine()` returns a
 * `ZodEffects`, which drops that. The ordering check is applied at the parse site via
 * `withOrderedDates`, after any extension.
 */
export const dashboardQuerySchema = z.object({
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  insuranceCompanyId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  insuranceType: z.enum(INSURANCE_TYPES).optional(),
  assignedAgentId: uuidSchema.optional(),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

export interface DashboardSummaryDto {
  totalPremium: string;
  totalCommission: string;
  activePolicies: number;
  upcomingRenewals: number;
  unpaidPolicies: number;
  openClaims: number;
  /** Period-over-period deltas, so a KPI tile can show direction rather than a bare number. */
  deltas: {
    totalPremium: number | null;
    totalCommission: number | null;
    activePolicies: number | null;
  };
  /** Retention = renewed ÷ (renewed + lapsed) over the filtered window. */
  retentionRate: number | null;
  averagePremium: string;
  outstandingReceivables: string;
  unreconciledCommission: string;
  /**
   * Live policies with no ΚΑΘΑΡΑ recorded, and therefore a commission of zero.
   *
   * Travels with the headline figures because it explains them: commission is calculated on the
   * net premium only, so while this count is high the commission tiles read low for a reason that
   * has nothing to do with the book.
   */
  policiesMissingNetPremium: number;
}

export interface TimeSeriesPoint {
  period: string;
  premium: string;
  commission: string;
  count: number;
}

export interface BreakdownRow {
  key: string;
  label: string;
  premium: string;
  commission: string;
  count: number;
}

export interface AgentPerformanceRow {
  agentId: string;
  agentName: string;
  policyCount: number;
  premium: string;
  commission: string;
  claimsHandled: number;
  renewalsCompleted: number;
  retentionRate: number | null;
}

/* ── the agent's daily worklist («Η μέρα μου») ──────────────────────────────── */

export interface TodayRenewalRow {
  policyId: string;
  policyNumber: string;
  clientId: string;
  clientName: string;
  phone: string | null;
  endDate: string;
  daysLeft: number;
  premiumAmount: string;
  insuranceType: string;
}

export interface TodayCollectionRow {
  installmentId: string;
  policyId: string;
  policyNumber: string;
  clientId: string;
  clientName: string;
  phone: string | null;
  dueDate: string;
  amount: string;
  installmentNumber: number;
  totalInstallments: number;
  daysOverdue: number;
  status: string;
}

export interface TodayReminderRow {
  id: string;
  title: string;
  reminderDate: string;
  priority: string;
  daysOverdue: number;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  relatedEntityLabel: string | null;
}

export interface TodayBirthdayRow {
  clientId: string;
  fullName: string;
  phone: string | null;
  dateOfBirth: string;
  nextBirthday: string;
  inDays: number;
  turnsAge: number;
}

export interface TodayExpiringDocumentRow {
  clientId: string;
  clientName: string;
  documentType: string;
  fileName: string;
  expiresAt: string;
  inDays: number;
}

export interface TodayDto {
  renewals: TodayRenewalRow[];
  collections: {
    rows: TodayCollectionRow[];
    overdueTotal: string;
    dueSoonTotal: string;
  };
  reminders: TodayReminderRow[];
  birthdays: TodayBirthdayRow[];
  expiringDocuments: TodayExpiringDocumentRow[];
  /** Win-back prompt: policies that lapsed unpaid in the last quarter. */
  lapsed: { count: number; premium: string };
}

/* ── search ─────────────────────────────────────────────────────────────────── */

export interface SearchHit {
  entity: 'client' | 'policy' | 'claim';
  id: string;
  title: string;
  subtitle: string;
  badge?: string;
}

export interface SearchResultsDto {
  clients: SearchHit[];
  policies: SearchHit[];
  claims: SearchHit[];
  total: number;
}

/* ── notifications (benchmark gap #8) ───────────────────────────────────────── */

export interface NotificationDto {
  id: string;
  notificationType: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
}

/* ── audit (benchmark gap #1) ───────────────────────────────────────────────── */

export const auditListQuerySchema = listQuerySchema.extend({
  entityType: z.string().max(50).optional(),
  entityId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  action: z.string().max(20).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
});

export interface AuditLogDto {
  id: string;
  entityType: string;
  entityId: string | null;
  action: string;
  /** Field-level before/after, so a reviewer sees exactly what moved. */
  changes: Record<string, { from: unknown; to: unknown }> | null;
  userId: string | null;
  userName: string | null;
  ipAddress: string | null;
  createdAt: string;
}

/* ── import (benchmark gap #10) ─────────────────────────────────────────────── */

export interface ImportRowError {
  row: number;
  field: string | null;
  message: string;
}

export interface ImportResultDto {
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  imported: number;
  skipped: number;
  errors: ImportRowError[];
}
